'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import { useLang } from '@/lib/langContext';
import { createClient } from '@/lib/supabase/client';

interface ForgotPasswordData {
  email: string;
}

interface Props {
  onBack: () => void;
}

export default function ForgotPasswordForm({ onBack }: Props) {
  const { dir } = useLang();
  const supabase = createClient();
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<ForgotPasswordData>({
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordData) => {
    setIsLoading(true);
    setError(null);
    try {
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${siteUrl}/auth/reset-password`,
      });
      if (resetError) throw resetError;
      setSent(true);
    } catch (err: any) {
      setError(
        err?.message ||
          (dir === 'rtl' ? 'حدث خطأ، حاول مرة أخرى' : 'An error occurred, please try again')
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="fade-in text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <Icon name="EnvelopeIcon" size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-800 text-foreground mb-3">
          {dir === 'rtl' ? 'تحقق من بريدك الإلكتروني' : 'Check your email'}
        </h2>
        <p className="text-muted-foreground text-base mb-2">
          {dir === 'rtl' ?'أرسلنا رابط إعادة تعيين كلمة المرور إلى' :'We sent a password reset link to'}
        </p>
        <p className="text-foreground font-600 mb-6">{getValues('email')}</p>
        <p className="text-sm text-muted-foreground mb-8">
          {dir === 'rtl' ?'انقر على الرابط في البريد الإلكتروني لإعادة تعيين كلمة المرور. قد يستغرق وصوله بضع دقائق.' :'Click the link in the email to reset your password. It may take a few minutes to arrive.'}
        </p>
        <button
          onClick={onBack}
          className="btn-primary w-full py-3 rounded-lg text-base font-600"
        >
          {dir === 'rtl' ? 'العودة إلى تسجيل الدخول' : 'Back to Sign In'}
        </button>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <Icon name={dir === 'rtl' ? 'ArrowRightIcon' : 'ArrowLeftIcon'} size={16} />
        {dir === 'rtl' ? 'العودة إلى تسجيل الدخول' : 'Back to Sign In'}
      </button>

      <div className="mb-8">
        <h2 className="text-3xl font-800 text-foreground mb-2">
          {dir === 'rtl' ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
        </h2>
        <p className="text-muted-foreground text-base">
          {dir === 'rtl' ?'أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين' : "Enter your email and we'll send you a reset link"}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2 text-sm text-red-700">
          <Icon name="ExclamationCircleIcon" size={16} className="flex-shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">
            {dir === 'rtl' ? 'البريد الإلكتروني' : 'Email Address'}
            <span className="text-status-expired ms-1">*</span>
          </label>
          <input
            type="email"
            autoComplete="email"
            placeholder={dir === 'rtl' ? 'example@company.com' : 'you@company.com'}
            className={`input-base w-full px-4 py-2.5 text-base ${errors.email ? 'border-status-expired' : ''}`}
            {...register('email', {
              required: dir === 'rtl' ? 'هذا الحقل مطلوب' : 'This field is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: dir === 'rtl' ? 'أدخل عنوان بريد إلكتروني صحيح' : 'Enter a valid email address',
              },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-sm text-status-expired flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              {errors.email.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 rounded-lg text-base font-600 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
              {dir === 'rtl' ? 'جاري الإرسال...' : 'Sending...'}
            </>
          ) : (
            dir === 'rtl' ? 'إرسال رابط إعادة التعيين' : 'Send Reset Link'
          )}
        </button>
      </form>
    </div>
  );
}
